import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getWorkspaceOwnershipRules,
  updateWorkspaceOwnershipRules,
} from "../../api/workspaceCompliance";
import WorkspaceComplianceFrame from "../../components/workspaceCompliance/WorkspaceComplianceFrame";
import {
  BooleanCard,
  fieldClass,
  InputLabel,
  SectionCard,
  WarningBanner,
} from "../../components/workspaceCompliance/WorkspaceComplianceUi";
import { useAuth } from "../../context/AuthContext";
import useOperatorWorkspace from "../../hooks/useOperatorWorkspace";
import {
  clientOwnershipOptions,
  occupancyModeOptions,
  paymentCollectorOptions,
  titleCaseComplianceLabel,
} from "../../constants/workspaceCompliance";
import { getAdvancedRecordTooltip } from "../../components/workspaceCompliance/advancedRecordsHelpContent";

const defaultRules = {
  default_occupancy_mode: "independent_owner",
  default_client_ownership_mode: "independent_owned",
  payment_collector: "renter",
  requires_agreement_acknowledgement: true,
  allow_renter_guest_checkout: false,
};

function OccupancyChoice({ option, selected, onSelect }) {
  const active = option.value === selected;
  const content = (
    <>
      <div className="text-sm font-semibold text-slate-900">{option.label}</div>
      <p className="mt-2 text-sm leading-6 text-slate-600">{option.description}</p>
      <div className="mt-4 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
        Current launch setting
      </div>
    </>
  );
  if (!onSelect) {
    return (
      <article
        className={`rounded-3xl border p-5 text-left ${
          active ? "border-rose-300 bg-rose-50 shadow-sm" : "border-slate-200 bg-white"
        }`}
      >
        {content}
      </article>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={`rounded-3xl border p-5 text-left transition ${
        active
          ? "border-rose-300 bg-rose-50 shadow-sm"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      {content}
    </button>
  );
}

function ExplanationCard({ title, description, effects }) {
  return (
    <article className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {effects.map((effect) => (
          <div
            key={effect.label}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-4"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {effect.label}
            </div>
            <div className="mt-2 text-sm text-slate-700">{effect.value}</div>
          </div>
        ))}
      </div>
    </article>
  );
}

export default function OperatorWorkspaceOwnershipPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const { workspace, error: workspaceError } = useOperatorWorkspace(id);
  const [rules, setRules] = useState(defaultRules);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!token || !id) return undefined;
    let active = true;
    setLoading(true);
    setError("");

    getWorkspaceOwnershipRules(id, token)
      .then((response) => {
        if (!active) return;
        setRules({
          ...defaultRules,
          ...(response || {}),
          default_occupancy_mode:
            response?.default_occupancy_mode || defaultRules.default_occupancy_mode,
          default_client_ownership_mode:
            response?.default_client_ownership_mode || "independent_owned",
        });
      })
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load workspace ownership rules.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rules.default_occupancy_mode) {
      setError("Choose the operating relationship before saving ownership rules.");
      return;
    }
    if (!token || !id) return;
    setSaving(true);
    setError("");
    setFlash("");
    try {
      const saved = await updateWorkspaceOwnershipRules(id, rules, token);
      setRules((prev) => ({
        ...prev,
        ...saved,
      }));
      setFlash("Client and payment control saved.");
    } catch (err) {
      setError(err.message || "Unable to save workspace ownership rules.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceComplianceFrame
      workspace={workspace}
      workspaceId={id}
      title="Client & Payment Control"
      description="Define who controls client records, payments, and agreement acknowledgement for this workspace."
    >
      {workspaceError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {workspaceError}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <WarningBanner
        strictModeEnabled={Boolean(rules.strict_mode_enabled)}
        publishBlocked={Boolean(rules.publish_blocked)}
        agreementCompletionBlocked={Boolean(rules.agreement_completion_blocked)}
        warningStateLevel={rules.warning_state_level || "none"}
        warnings={rules.warnings || []}
        disclaimer
      />

      <form className="space-y-6" onSubmit={handleSubmit}>
        <SectionCard
          title="Rental Relationship"
          description="For launch, MyBoothShare is set up for independent professionals renting workspace from a salon. This keeps the rental agreement, client ownership, payment responsibility, and support records tied to the right renter."
          tooltip={getAdvancedRecordTooltip("Operating relationship")}
        >
          {loading ? <p className="text-sm text-slate-600">Loading ownership defaults...</p> : null}
          <div className="grid gap-4 lg:grid-cols-3">
            {occupancyModeOptions.map((option) => (
              <OccupancyChoice
                key={option.value}
                option={option}
                selected={rules.default_occupancy_mode}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="Client & Payment Defaults"
          description="These defaults tell MyBoothShare how booking control, CRM visibility, and payout context should be interpreted when the workspace is rented."
          tooltip={getAdvancedRecordTooltip("Client & Payment Control")}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <InputLabel label="Client ownership mode" tooltip={getAdvancedRecordTooltip("Client ownership mode")}>
              <select
                className={fieldClass}
                value={rules.default_client_ownership_mode}
                onChange={(event) =>
                  setRules((prev) => ({
                    ...prev,
                    default_client_ownership_mode: event.target.value,
                  }))
                }
              >
                {clientOwnershipOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel label="Payment collector" tooltip={getAdvancedRecordTooltip("Payment collector")}>
              <select
                className={fieldClass}
                value={rules.payment_collector}
                onChange={(event) =>
                  setRules((prev) => ({
                    ...prev,
                    payment_collector: event.target.value,
                  }))
                }
              >
                {paymentCollectorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
          </div>
          <div className="grid gap-4">
            <BooleanCard
              label="Require agreement acknowledgement before activation?"
              tooltip={getAdvancedRecordTooltip("Require agreement acknowledgement before activation?")}
              checked={Boolean(rules.requires_agreement_acknowledgement)}
              onChange={(value) =>
                setRules((prev) => ({
                  ...prev,
                  requires_agreement_acknowledgement: value,
                }))
              }
            />
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              MyBoothShare rentals are for signed-in independent professionals. Each renter should use their own MyBoothShare account so the rental agreement, client ownership, payment responsibility, and setup history stay connected to the right person. One-time guest checkout is not available for launch.
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Read-Only Control Effects"
          description="Use these explanations to align the salon team around what each ownership choice changes across booking, CRM access, and payouts."
          tooltip="These cards explain what each control choice means across booking, CRM, and payout context."
        >
          <ExplanationCard
            title="Salon-owned clients"
            description="Choose this when the salon keeps the primary client relationship and expects the shared platform to reflect that operating setup."
            effects={[
              { label: "Booking", value: "Bookings are treated as part of the salon's operating structure." },
              { label: "CRM", value: "Client records stay anchored to salon access and reporting." },
              { label: "Payout", value: "Commercial flows tend to settle back through the salon context." },
            ]}
          />
          <ExplanationCard
            title="Independent-owned clients"
            description="Choose this when the renter operates as an independent business and should retain the primary client relationship over time."
            effects={[
              { label: "Booking", value: "Bookings are interpreted as part of the renter's independent operating model." },
              { label: "CRM", value: "Client control and visibility should be scoped to the independent professional where supported." },
              { label: "Payout", value: "Payment and ledger interpretation can flow toward the renter or independent professional path." },
            ]}
          />
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
            Current defaults: {titleCaseComplianceLabel(rules.default_occupancy_mode)} occupancy,{" "}
            {titleCaseComplianceLabel(rules.default_client_ownership_mode)} client ownership,{" "}
            {titleCaseComplianceLabel(rules.payment_collector)} payment collector.
          </div>
        </SectionCard>

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Client & Payment Control"}
          </button>
        </div>
      </form>
    </WorkspaceComplianceFrame>
  );
}
